/**
 * Email-related API service functions
 * These functions handle email confirmation, password reset, and studio invitations
 */

import apiClient from '../utils/apiClient';

const configuredApiUrl = (
  process.env.REACT_APP_API_URL || 'http://localhost:8000/api'
).replace(/\/+$/, '');
// Both historical conventions exist in deployments: origin-only and an
// origin ending in /api. Normalize before appending endpoint paths.
const API_ORIGIN = configuredApiUrl.endsWith('/api')
  ? configuredApiUrl.slice(0, -4)
  : configuredApiUrl;

const apiRequest = async (path, options = {}, { authenticated = false } = {}) => {
  const request = authenticated
    ? apiClient.fetch.bind(apiClient)
    : window.fetch.bind(window);
  const response = await request(`${API_ORIGIN}${path}`, {
    credentials: 'include',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {})
    }
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data.detail || data.message || 'Request failed');
    error.status = response.status;
    error.data = data;
    throw error;
  }
  return data;
};

/**
 * Confirm email address with token from email link
 * @param {string} token - The confirmation token from the email
 * @param {string} type - The type of confirmation (signup, email_change, etc.)
 */
export const confirmEmail = async (token, type = 'signup') => {
  try {
    return await apiRequest('/api/auth/confirm-email', {
      method: 'POST',
      body: JSON.stringify({ token, type })
    });
  } catch (error) {
    console.error('Email confirmation error:', error);
    return {
      success: false,
      message: error.data?.detail || error.data?.message || error.message || 'Failed to confirm email'
    };
  }
};

/**
 * Request password reset email
 * @param {string} email - The user's email address
 */
export const requestPasswordReset = async (email) => {
  try {
    return await apiRequest('/api/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email })
    });
  } catch (error) {
    console.error('Password reset request error:', error);
    return {
      success: false,
      message: error.data?.detail || error.data?.message || error.message || 'Failed to send reset email'
    };
  }
};

/**
 * Update password with reset token
 * @param {string} token - The reset token from the email
 * @param {string} newPassword - The new password
 */
export const updatePassword = async (token, newPassword) => {
  try {
    return await apiRequest('/api/auth/complete-password-reset', {
      method: 'POST',
      body: JSON.stringify({ access_token: token, password: newPassword })
    });
  } catch (error) {
    console.error('Password update error:', error);
    return {
      success: false,
      message: error.data?.detail || error.data?.message || error.message || 'Failed to update password'
    };
  }
};

/**
 * Accept studio invitation
 * @param {string} token - The invitation token
 */
export const acceptStudioInvitation = async (token) => {
  try {
    return await apiRequest(
      `/api/studio/invitations/token/${token}/respond`,
      {
        method: 'POST',
        body: JSON.stringify({ accept: true })
      },
      { authenticated: true }
    );
  } catch (error) {
    console.error('Accept invitation error:', error);
    return {
      success: false,
      message: error.data?.detail || error.data?.message || error.message || 'Failed to accept invitation'
    };
  }
};

/**
 * Decline studio invitation
 * @param {string} token - The invitation token
 */
export const declineStudioInvitation = async (token) => {
  try {
    return await apiRequest(
      `/api/studio/invitations/token/${token}/respond`,
      {
        method: 'POST',
        body: JSON.stringify({ accept: false })
      },
      { authenticated: true }
    );
  } catch (error) {
    console.error('Decline invitation error:', error);
    return {
      success: false,
      message: error.data?.detail || error.data?.message || error.message || 'Failed to decline invitation'
    };
  }
};

/**
 * Check if user has pending studio invitation
 * @param {string} email - The user's email
 */
export const getStudioInvitation = async (token) => {
  try {
    return await apiRequest(`/api/studio/invitations/token/${token}`, {
      method: 'GET'
    });
  } catch (error) {
    console.error('Invitation lookup error:', error);
    return {
      success: false,
      status: error.status,
      message: error.data?.detail || error.message || 'Invitation not found'
    };
  }
};

// Kept as a compatibility alias for callers that have not moved to token lookup.
// Email-based public invitation discovery was intentionally removed.
export const checkPendingInvitation = async () => ({
  success: false,
  hasPendingInvitation: false
});

/**
 * Resend confirmation email
 * @param {string} email - The user's email address
 */
export const resendConfirmationEmail = async (email) => {
  try {
    return await apiRequest('/api/auth/resend-confirmation', {
      method: 'POST',
      body: JSON.stringify({ email })
    });
  } catch (error) {
    console.error('Resend confirmation error:', error);
    return {
      success: false,
      message: error.data?.detail || error.data?.message || error.message || 'Failed to resend confirmation email'
    };
  }
};

const emailApi = {
  confirmEmail,
  requestPasswordReset,
  updatePassword,
  getStudioInvitation,
  acceptStudioInvitation,
  declineStudioInvitation,
  checkPendingInvitation,
  resendConfirmationEmail
};

export default emailApi;
